import { ReactElement, useEffect, useState } from 'react';
import { SpinnerContainer } from 'src/components/SpinnerContainer';
import { useMenus } from 'src/hooks/useMenus';
import { useUserTracking } from 'src/hooks/useUserTracking/useUserTracking';
import { VenueSummary } from '../../components/VenueSummary/VenueSummary';
import {
  BreadcrumbHeading,
  Card,
  CardGrid,
  ChannelIcon,
  DownloadIcon,
  EllipsisMenu,
  IconButton,
  MenuIcon,
  Plus,
  SnackbarStatus,
  Spinner,
  Typography,
  Upload,
  useSnackbar,
  VenueIcon,
} from '@weareopenr/component-library';
import { Link, useNavigate } from 'react-router-dom';
import EmptyState from '../../components/EmptyState';
import styles from './LegacyDashboard.module.scss';
import VenuesModal from '../../components/VenuesModal';
import MenuModal from '../../components/MenuModal';
import MenuCard from '../../components/MenuCard';
import { CreateVenueModal } from '../../components/CreateVenueModal/CreateVenueModal';
import { IMenu } from 'src/types/menu.types';
import { DashboardSectionHeader } from 'src/components/DashboardSectionHeader/DashboardSectionHeader';
import { ExternalSystemImages } from '../../constants';
import { useVenues } from 'src/hooks/useVenues/useVenues';
import { useExternalSystems } from 'src/hooks/useExternalSystems/useExternalSystems';
import createAddressSummary from '../Venue/createAddressSummary';
import { downloadVenuesTemplateCSV } from 'src/api/downloadVenuesTemplateCSV/downloadVenuesTemplateCSV';
import { saveAs } from 'file-saver';

export const LegacyDashboard = (): ReactElement => {
  const { data: menus, isFetching: isMenusFetching } = useMenus();
  const { data: venues, isLoading: isVenuesLoading } = useVenues();
  const { data: externalSystems, isLoading: isExternalSystemsLoading } = useExternalSystems();
  const navigate = useNavigate();
  const [isImportVenuesModalOpen, setIsImportVenuesModalOpen] = useState<boolean>(false);
  const [isMenuModalOpen, setIsMenuModalOpen] = useState<boolean>(false);
  const [isCreateVenueModalVisible, setIsCreateVenueModalVisible] = useState<boolean>(false);
  const { showSnackbar } = useSnackbar();
  const { trackEvent } = useUserTracking();

  useEffect(() => {
    document.title = 'Dashboard';
    trackEvent('page_viewed', { page: 'dashboard' });
  }, []);

  const handleCreateVenueModalToggle = (): void => {
    setIsCreateVenueModalVisible((prev) => !prev);
  };

  const handleDownload = () => {
    downloadVenuesTemplateCSV()
      .then((response) => {
        saveAs(response, `template.csv`);
      })
      .catch(() => {
        showSnackbar({ status: SnackbarStatus.ERROR, message: 'Internal Server Error' });
      });
  };

  return (
    <div data-testid="Wrapper" className={styles.wrapper}>
      {isMenusFetching || isVenuesLoading || isExternalSystemsLoading ? (
        <SpinnerContainer>
          <Spinner size="xlarge" colour="rainbow" />
        </SpinnerContainer>
      ) : (
        <>
          <>
            <CreateVenueModal closeModal={handleCreateVenueModalToggle} open={isCreateVenueModalVisible} />
            <VenuesModal open={isImportVenuesModalOpen} closeModal={() => setIsImportVenuesModalOpen(false)} />
            <BreadcrumbHeading breadcrumbs={[{ label: 'Dashboard', path: '/' }]} />
            <DashboardSectionHeader title="Menus">
              <IconButton
                aria-label="Create menu"
                onClick={() => setIsMenuModalOpen(true)}
                icon={Plus}
                buttonStyle="ghost"
              />
              {menus?.length >= 5 && (
                <Link to="/menus" className={styles['link']} role="link">
                  <Typography size="medium" className={styles['link']} weight="bold">
                    See all Menus
                  </Typography>
                </Link>
              )}
            </DashboardSectionHeader>
            <MenuModal
              image={null}
              title="Create new menu"
              closeModal={() => setIsMenuModalOpen(false)}
              open={isMenuModalOpen}
              variant="create"
            />
            {menus?.length > 0 ? (
              <CardGrid>
                {menus?.slice(0, 5).map((menu: IMenu) => {
                  const { menuId: menuID, name, image, consumerName } = menu;
                  return (
                    <MenuCard menuId={menuID} name={name} image={image} key={menuID} consumerName={consumerName} />
                  );
                })}
              </CardGrid>
            ) : (
              <EmptyState
                emptySectionText="Add your first menu"
                onClick={() => setIsMenuModalOpen(true)}
                buttonText="Create menu"
                type="menu"
                Icon={MenuIcon}
              />
            )}
          </>

          <DashboardSectionHeader title="Venues">
            <EllipsisMenu
              options={[
                {
                  id: 'create-venue',
                  name: 'Create venue',
                  onOptionClick: handleCreateVenueModalToggle,
                  icon: Plus,
                },
                {
                  id: 'import-venues',
                  name: 'Import venues',
                  onOptionClick: () => setIsImportVenuesModalOpen(true),
                  icon: Upload,
                },
                {
                  id: 'download-template',
                  name: 'Download template',
                  onOptionClick: handleDownload,
                  icon: DownloadIcon,
                },
              ]}
              variant="ghost"
            />
            {venues?.length >= 5 && (
              <Link to="/venues" className={styles['link']} role="link">
                <Typography size="medium" className={styles['link']} weight="bold">
                  See all Venues
                </Typography>
              </Link>
            )}
          </DashboardSectionHeader>

          <CardGrid>
            {venues
              .slice()
              .sort((a, b) => new Date(b.creation_date).valueOf() - new Date(a.creation_date).valueOf())
              .slice(0, 5)
              .map((venue) => (
                <div key={venue.venue_id}>
                  <VenueSummary
                    title={venue.name}
                    venueId={venue.venue_id}
                    subtitle={createAddressSummary(venue.addresses[0])}
                    image={venue?.image_url}
                    onViewClick={() => {
                      navigate(`/venues/${venue.venue_id}`);
                    }}
                  />
                </div>
              ))}
          </CardGrid>
          {venues.length === 0 && (
            <EmptyState
              emptySectionText="Add your first venue"
              type="venues"
              Icon={VenueIcon}
              buttonText="Create venue"
              onClick={handleCreateVenueModalToggle}
            />
          )}
          <DashboardSectionHeader title="Channels" />
          <CardGrid>
            {externalSystems.map((aggregator) => {
              const image = ExternalSystemImages[aggregator.name];
              return (
                <Card
                  text={aggregator.name}
                  subtext={aggregator.integrationType}
                  key={aggregator.aggregatorId}
                  imageSrc={image}
                  icon={ChannelIcon}
                  variant="secondary"
                />
              );
            })}
          </CardGrid>
          {externalSystems.length === 0 && (
            <EmptyState emptySectionText="No channels available yet" type="channels" Icon={ChannelIcon} />
          )}
        </>
      )}
    </div>
  );
};
